import { HelpChat } from "./HelpChat";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, Tag, User, Shield, LogOut, Crown, Pencil, Check, Heart, Megaphone, Sparkles, Gift, BarChart3 } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import Footer from "./Footer";
import api from "../lib/apiClient";
import { HIDE_SALES, HIDE_LATEST_DEALS, PRELAUNCH_FREE } from "../lib/siteConfig";

const ROW = "w-full flex items-center gap-4 px-4 rounded-2xl text-left transition-all";

// One shared row: fixed icon column (w-8) + text block, so every title/description starts on the same left edge.
function MenuItem({ icon: Icon, title, desc, onClick, testId, className, trailing }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`${ROW} ${className}`}>
      <span className="w-8 shrink-0 flex items-center justify-center"><Icon size={26} strokeWidth={2} /></span>
      <span className="flex-1 min-w-0">
        <span className="block font-heading font-extrabold text-lg leading-tight">{title}</span>
        {desc && <span className="block text-sm font-body font-medium leading-snug mt-0.5 opacity-80">{desc}</span>}
      </span>
      {trailing}
    </button>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { isAdmin, editMode, setEditMode } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const menuBtnRef = useRef(null);

  const closeMenu = useCallback(() => {
    setOpen(false);
    // Return focus to the Know Me trigger for keyboard/screen-reader users.
    if (menuBtnRef.current) menuBtnRef.current.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closeMenu(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeMenu]);

  const loadUnread = useCallback(() => {
    if (!user) { setUnread(0); return; }
    api.get("/alerts/unread-count").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
  }, [user]);

  useEffect(() => { loadUnread(); }, [loadUnread, location.pathname]);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <div className={`min-h-screen bg-cream${editMode ? " uptoo-editing" : ""}`}>
      <style>{`
        .uptoo-editable{outline:1.5px dashed rgba(255,77,109,.55);outline-offset:3px;border-radius:6px;cursor:text;transition:background .15s,outline-color .15s}
        .uptoo-editable:hover{background:rgba(255,77,109,.06);outline-color:#FF4D6D}
        .uptoo-editable:focus{background:rgba(255,77,109,.09);outline:2px solid #FF4D6D;box-shadow:0 0 0 4px rgba(255,77,109,.12)}
      `}</style>

      {/* Top bar: logo + Find Me · Ding Me · Know Me (Know Me opens the account sheet) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:bg-brand focus:text-white focus:px-4 focus:py-2 focus:rounded-full" data-testid="skip-to-content">Skip to content</a>
      <header className="sticky top-0 z-[90] bg-white/90 backdrop-blur-md border-b border-line">
        <nav aria-label="Primary" className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center" aria-label="SIBLINGO home" data-testid="nav-logo-btn">
            <Logo height="h-10 sm:h-11 md:h-14" withTagline taglineClassName="hidden sm:block text-[9px] md:text-[11px] mt-1 tracking-[0.16em]" />
          </button>
          <div className="flex items-center gap-0.5 sm:gap-1" data-testid="header-primary-links">
            {[["/family-deals", "Find Me", "header-find-me"], ["/notify-me", "Ding Me", "header-ding-me"]].map(([to, label, tid]) => (
              <button key={to} onClick={() => navigate(to)} data-testid={tid} aria-current={location.pathname.startsWith(to) ? "page" : undefined} className={`relative h-10 sm:h-11 px-2.5 sm:px-4 rounded-full font-heading font-extrabold text-[13px] sm:text-sm whitespace-nowrap transition-colors ${location.pathname.startsWith(to) ? "bg-blush text-brand" : "text-ink hover:bg-cream"}`}>
                {label}
                {tid === "header-ding-me" && unread > 0 && (
                  <span data-testid="menu-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-black flex items-center justify-center border-2 border-white">{unread > 99 ? "99+" : unread}</span>
                )}
              </button>
            ))}
            <button
              ref={menuBtnRef}
              onClick={() => setOpen((v) => !v)}
              data-testid="header-know-me"
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls="side-panel"
              aria-current={location.pathname.startsWith("/me") ? "page" : undefined}
              className={`flex items-center gap-1.5 h-10 sm:h-11 px-3 sm:px-4 rounded-full font-heading font-extrabold text-[13px] sm:text-sm whitespace-nowrap transition-colors ${open || location.pathname.startsWith("/me") ? "bg-ink text-white" : "bg-ink/5 text-ink hover:bg-cream"}`}
            >
              <User size={16} /> Know Me
            </button>
          </div>
        </nav>
      </header>

      {/* Prelaunch: everything available today is free — no trial clock, no upgrade pressure. Trial banners return when PRELAUNCH_FREE is off. */}
      {user && PRELAUNCH_FREE && ["trial", "trial_expired"].includes(user.plan_status) && (
        <div data-testid="prelaunch-banner" className="bg-blush text-ink border-b border-line">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center justify-center gap-2 flex-wrap text-center">
            <span className="text-sm font-semibold flex items-center gap-2"><Sparkles size={16} className="text-brand" /> Sizing assistance and saved searches are free during our prelaunch — nothing to unlock.</span>
          </div>
        </div>
      )}
      {user && !PRELAUNCH_FREE && user.plan_status === "trial" && (
        <div data-testid="trial-banner" className="bg-gradient-to-r from-brand to-gold text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center justify-center gap-3 flex-wrap text-center">
            <span className="text-sm font-semibold flex items-center gap-2">
              <Crown size={16} /> You're on a free trial — <b>{user.trial_days_remaining} {user.trial_days_remaining === 1 ? "day" : "days"} remaining</b>. Browsing, sizing assistance and saved searches stay free afterwards.
            </span>
            <button onClick={() => navigate("/upgrade")} data-testid="trial-banner-upgrade" className="h-8 px-4 rounded-full bg-white text-brand font-bold text-xs hover:bg-white/90 transition-all">
              About Premium
            </button>
          </div>
        </div>
      )}
      {user && !PRELAUNCH_FREE && user.plan_status === "trial_expired" && (
        <div data-testid="trial-ended-banner" className="bg-ink text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center justify-center gap-3 flex-wrap text-center">
            <span className="text-sm font-medium">
              Your free trial has ended — you still have full access to browse collections, sizing assistance and saved searches. Matches for your saved searches appear in Ding Me.
            </span>
            <button onClick={() => navigate("/upgrade")} data-testid="trial-ended-upgrade" className="h-8 px-4 rounded-full bg-gold text-white font-bold text-xs hover:bg-gold-hover transition-all">
              Unlock purchase links
            </button>
          </div>
        </div>
      )}

      <main id="main-content" tabIndex={-1} className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">{children}</main>
      <HelpChat />

      <Footer />

      {/* Side panel */}
      {open && (
        <>
          <div data-testid="side-panel-overlay" onClick={closeMenu} className="fixed left-0 right-0 bottom-0 top-20 z-[70] bg-ink/40 backdrop-blur-sm" />
          <aside id="side-panel" data-testid="side-panel" role="dialog" aria-modal="true" aria-label="Know Me" className="fixed top-20 right-0 z-[80] h-[calc(100%-5rem)] w-[86%] max-w-sm bg-white shadow-lift flex flex-col animate-[slideIn_.22s_ease-out]">
            <style>{`@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>
            <div className="flex items-center justify-between px-5 h-16 border-b border-line">
              <p className="font-heading font-extrabold text-ink inline-flex items-center gap-2"><User size={18} className="text-brand" /> Know Me{user && <span className="text-xs font-semibold text-ink-muted truncate max-w-[160px]" data-testid="panel-user-email">· {user.email}</span>}</p>
              <button onClick={closeMenu} data-testid="close-menu-btn" aria-label="Close Know Me" className="p-2 rounded-full text-ink-soft hover:bg-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"><X size={22} /></button>
            </div>

            <div className="px-4 pt-5 pb-4 overflow-y-auto">
              <MenuItem onClick={() => go("/me")} testId="panel-me-btn" icon={User} title="Family & preferences" desc="Children, sizes, saved family and settings" className="py-4 bg-brand text-white hover:bg-brand-hover" />
              {!HIDE_SALES && (
                <div className="mt-3 space-y-1.5">
                  <MenuItem onClick={() => go("/sales")} testId="panel-sales-btn" icon={Tag} title="Local Sales" className="py-3 bg-blush text-brand border border-brand/20 hover:bg-brand-light" />
                  <MenuItem onClick={() => go("/giveaways")} testId="panel-giveaways-btn" icon={Gift} title="Free Giveaways" className="py-3 bg-gold/90 text-white hover:bg-gold-hover" />
                </div>
              )}

              {/* Latest Deals — standalone public page */}
              {!HIDE_LATEST_DEALS && (
                <MenuItem onClick={() => go("/deals")} testId="panel-latest-deals-btn" icon={Sparkles} title="Latest Deals" className="mt-3 py-4 bg-ink text-white hover:bg-ink/90" />
              )}

              <div className="mt-4 space-y-1">
                {user && (user.affiliate || user.role === "admin") && (
                  <MenuItem onClick={() => go("/partner-desk")} testId="panel-partner-desk-btn" icon={BarChart3} title="Partner desk" desc="Merchant connections & partner steps" className="py-3.5 text-ink hover:bg-cream [&_svg]:text-brand" />
                )}
                {user && (
                  <MenuItem onClick={() => go("/studio")} testId="panel-studio-btn" icon={Pencil} title="Contributor studio" desc="Your looks, reviews and earnings" className="py-3.5 text-ink hover:bg-cream [&_svg]:text-brand" />
                )}
                {!PRELAUNCH_FREE && user && (user.affiliate || user.role === "admin") && (
                  <MenuItem onClick={() => go("/post-deal")} testId="panel-post-deal-btn" icon={Megaphone} title="Post a Deal" className="py-4 bg-ink text-white hover:bg-ink-soft" />
                )}
                {!PRELAUNCH_FREE && user && (user.affiliate || user.role === "admin") && (
                  <MenuItem onClick={() => go("/merchant-stats")} testId="panel-merchant-stats-btn" icon={BarChart3} title="Deal Statistics" className="py-3.5 text-ink hover:bg-cream [&_svg]:text-brand" />
                )}
                {!PRELAUNCH_FREE && user && (
                  <MenuItem onClick={() => go("/loved")} testId="panel-loved-btn" icon={Heart} title="Saved collections" className="py-3.5 text-ink hover:bg-cream [&_svg]:text-brand" />
                )}
                {user && user.role === "admin" && (
                  <MenuItem onClick={() => go("/admin")} testId="panel-admin-btn" icon={Shield} title="Admin" className="py-3.5 text-ink hover:bg-cream [&_svg]:text-brand" />
                )}
              </div>
            </div>

            <div className="mt-auto p-5 border-t border-line space-y-2">
              {!PRELAUNCH_FREE && user && !["premium", "comp"].includes(user.plan_status) && (
                <button onClick={() => go("/upgrade")} data-testid="panel-upgrade-btn" className="w-full flex items-center justify-center gap-2 h-12 rounded-full bg-gold text-white font-bold hover:bg-gold-hover transition-all">
                  <Crown size={18} /> Upgrade to Premium
                </button>
              )}
              {user ? (
                <button onClick={() => { logout(); go("/"); }} data-testid="panel-logout-btn" className="w-full flex items-center justify-center gap-2 h-12 rounded-full border-2 border-line text-ink-soft font-bold hover:border-brand/30 transition-all">
                  <LogOut size={18} /> Log out
                </button>
              ) : (
                <button onClick={() => go("/auth")} data-testid="panel-login-btn" className="w-full flex items-center justify-center gap-2 h-12 rounded-full bg-brand text-white font-bold hover:bg-brand-hover transition-all">
                  Sign In
                </button>
              )}
            </div>
          </aside>
        </>
      )}

      {isAdmin && (
        <button
          onClick={() => setEditMode((v) => !v)}
          data-testid="edit-mode-toggle"
          className={`fixed z-[60] bottom-6 right-4 md:right-6 flex items-center gap-2 px-4 h-12 rounded-full shadow-lift font-bold text-sm transition-all ${
            editMode ? "bg-success text-white" : "bg-ink text-white hover:bg-ink/90"
          }`}
          title={editMode ? "Editing is ON — click any text to change it" : "Turn on tap-to-edit"}
        >
          {editMode ? <Check size={18} /> : <Pencil size={18} />}
          {editMode ? "Done editing" : "Edit text"}
        </button>
      )}
    </div>
  );
}
